/**
 * Global Search Service
 * Provides unified search functionality across Dashboard and DOM pages
 * Supports smart search patterns like:
 * - "NIFTY" - All NIFTY instruments
 * - "25600" - All options with strike 25600
 * - "25600 CE" - All CE options with strike 25600
 * - "NIFTY 25600 CE" - NIFTY CE options with strike 25600
 * - "BANKNIFTY FUT" - All BANKNIFTY futures
 * 
 * Results organized: EQ → FUT → OPTIONS (CE → PE, nearest expiry first)
 */

// Parse search query into components
export const parseSearchQuery = (query) => {
  if (!query) return { raw: '', terms: [] };
  
  const normalized = query.toUpperCase().trim();
  const terms = normalized.split(/\s+/).filter(t => t.length > 0);
  
  const result = {
    raw: normalized,
    terms,
    underlying: null,    // NIFTY, BANKNIFTY, RELIANCE, etc.
    strike: null,        // 25600, 50000, etc.
    optionType: null,    // CE, PE
    instrumentType: null, // EQ, FUT, OPT
    expiry: null         // Weekly, Monthly, specific date
  };
  
  // Identify components
  terms.forEach(term => {
    // Check for strike price (all digits, 3-6 digits)
    if (/^\d{3,6}$/.test(term)) {
      result.strike = parseInt(term);
      return;
    }
    
    // Check for option type
    if (term === 'CE' || term === 'CALL') {
      result.optionType = 'CE';
      return;
    }
    if (term === 'PE' || term === 'PUT') {
      result.optionType = 'PE';
      return;
    }
    
    // Check for instrument type
    if (term === 'EQ' || term === 'EQUITY' || term === 'STOCK') {
      result.instrumentType = 'EQ';
      return;
    }
    if (term === 'FUT' || term === 'FUTURE' || term === 'FUTURES') {
      result.instrumentType = 'FUT';
      return;
    }
    if (term === 'OPT' || term === 'OPTION' || term === 'OPTIONS') {
      result.instrumentType = 'OPT';
      return;
    }
    
    // Check for expiry hints
    if (term === 'WEEKLY' || term === 'WK') {
      result.expiry = 'WEEKLY';
      return;
    }
    if (term === 'MONTHLY' || term === 'MTH') {
      result.expiry = 'MONTHLY';
      return;
    }
    if (term === 'NEAR' || term === 'NEAREST') {
      result.expiry = 'NEAREST';
      return;
    }
    if (term === 'FAR') {
      result.expiry = 'FAR';
      return;
    }
    
    // Otherwise, it's likely the underlying symbol
    if (!result.underlying && term.length >= 2) {
      result.underlying = term;
    }
  });
  
  return result;
};

// Get expiry date from symbol (Zerodha format: NIFTY24FEB25600CE)
export const parseExpiryFromSymbol = (symbol) => {
  if (!symbol) return null;
  
  // Zerodha format: NIFTY24FEB25600CE or NIFTY2420225600CE
  // Dhan format might be different
  const match = symbol.match(/(\d{2})([A-Z]{3}|\d{3})(\d+)/);
  if (match) {
    const year = `20${match[1]}`;
    const monthOrDay = match[2];
    
    // Convert month abbreviation to date
    const months = {
      'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
      'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
      'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
    };
    
    if (months[monthOrDay]) {
      // Monthly expiry - last Thursday of month
      return new Date(`${year}-${months[monthOrDay]}-28`);
    } else {
      // Weekly expiry - day of month
      const month = new Date().getMonth() + 1;
      const day = parseInt(monthOrDay);
      return new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    }
  }
  
  return null;
};

// Sort instruments by type and expiry
export const sortInstruments = (instruments) => {
  const typeOrder = {
    'INDEX': 0,
    'EQ': 1,
    'FUTIDX': 2,
    'FUTSTK': 3,
    'FUT': 4,
    'OPTIDX': 5,
    'OPTSTK': 6,
    'CE': 7,
    'PE': 8
  };
  
  return [...instruments].sort((a, b) => {
    const typeA = a.instrument_type || a.segment || 'ZZZ';
    const typeB = b.instrument_type || b.segment || 'ZZZ';
    
    // First sort by instrument type
    const orderA = typeOrder[typeA] ?? 99;
    const orderB = typeOrder[typeB] ?? 99;
    
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    
    // For options, sort CE before PE
    if (typeA === 'CE' && typeB === 'PE') return -1;
    if (typeA === 'PE' && typeB === 'CE') return 1;
    
    // Then sort by expiry (nearest first)
    const expiryA = a.expiry ? new Date(a.expiry) : null;
    const expiryB = b.expiry ? new Date(b.expiry) : null;
    
    if (expiryA && expiryB) {
      const diff = expiryA - expiryB;
      if (diff !== 0) return diff;
    }
    
    // Then by strike price
    const strikeA = a.strike || 0;
    const strikeB = b.strike || 0;
    if (strikeA !== strikeB) return strikeA - strikeB;
    
    // Finally by symbol name
    const symA = a.tradingsymbol || a.trading_symbol || '';
    const symB = b.tradingsymbol || b.trading_symbol || '';
    return symA.localeCompare(symB);
  });
};

// Group instruments by category
export const groupInstruments = (instruments) => {
  const groups = {
    indices: [],
    equity: [],
    futures: [],
    options_ce: [],
    options_pe: []
  };
  
  instruments.forEach(inst => {
    const type = inst.instrument_type || inst.segment || '';
    const symbol = inst.tradingsymbol || inst.trading_symbol || '';
    
    if (type === 'INDEX' || type === 'IDX_I' || inst.segment === 'INDICES') {
      groups.indices.push(inst);
    } else if (type === 'EQ' || type === 'NSE_EQ' || type === 'BSE_EQ' || inst.segment === 'NSE_EQ') {
      groups.equity.push(inst);
    } else if (type.includes('FUT') || type === 'FUTIDX' || type === 'FUTSTK') {
      groups.futures.push(inst);
    } else if (type === 'CE' || type.includes('OPT') && symbol.endsWith('CE')) {
      groups.options_ce.push(inst);
    } else if (type === 'PE' || type.includes('OPT') && symbol.endsWith('PE')) {
      groups.options_pe.push(inst);
    } else if (symbol.endsWith('CE')) {
      groups.options_ce.push(inst);
    } else if (symbol.endsWith('PE')) {
      groups.options_pe.push(inst);
    } else {
      // Default to equity if unknown
      groups.equity.push(inst);
    }
  });
  
  // Sort each group
  Object.keys(groups).forEach(key => {
    groups[key] = sortInstruments(groups[key]);
  });
  
  return groups;
};

// Filter instruments based on parsed query
export const filterInstruments = (instruments, parsedQuery) => {
  if (!instruments || instruments.length === 0) return [];
  
  let filtered = [...instruments];
  
  // Filter by underlying
  if (parsedQuery.underlying) {
    filtered = filtered.filter(inst => {
      const symbol = (inst.tradingsymbol || inst.trading_symbol || inst.name || '').toUpperCase();
      const name = (inst.name || inst.company || '').toUpperCase();
      return symbol.includes(parsedQuery.underlying) || name.includes(parsedQuery.underlying);
    });
  }
  
  // Filter by strike price
  if (parsedQuery.strike) {
    filtered = filtered.filter(inst => {
      const symbol = (inst.tradingsymbol || inst.trading_symbol || '').toUpperCase();
      const strike = inst.strike || 0;
      // Check if strike matches or symbol contains the strike
      return strike === parsedQuery.strike || symbol.includes(String(parsedQuery.strike));
    });
  }
  
  // Filter by option type (CE/PE)
  if (parsedQuery.optionType) {
    filtered = filtered.filter(inst => {
      const symbol = (inst.tradingsymbol || inst.trading_symbol || '').toUpperCase();
      const type = inst.instrument_type || '';
      return symbol.endsWith(parsedQuery.optionType) || type === parsedQuery.optionType;
    });
  }
  
  // Filter by instrument type
  if (parsedQuery.instrumentType) {
    filtered = filtered.filter(inst => {
      const type = (inst.instrument_type || inst.segment || '').toUpperCase();
      const symbol = (inst.tradingsymbol || inst.trading_symbol || '').toUpperCase();
      
      if (parsedQuery.instrumentType === 'EQ') {
        return type === 'EQ' || type === 'NSE_EQ' || type === 'BSE_EQ';
      }
      if (parsedQuery.instrumentType === 'FUT') {
        return type.includes('FUT');
      }
      if (parsedQuery.instrumentType === 'OPT') {
        return type.includes('OPT') || symbol.endsWith('CE') || symbol.endsWith('PE');
      }
      return true;
    });
  }
  
  return filtered;
};

// Main search function
export const globalSearch = (instruments, query, options = {}) => {
  const { limit = 100, sortByRelevance = true } = options;
  
  if (!query || query.trim().length < 2) {
    return { instruments: [], grouped: groupInstruments([]), query: '' };
  }
  
  const parsedQuery = parseSearchQuery(query);
  
  // If only strike is provided, it's likely an options search
  if (parsedQuery.strike && !parsedQuery.underlying && !parsedQuery.raw.match(/[A-Z]/)) {
    // Search for all options with this strike
    parsedQuery.instrumentType = 'OPT';
  }
  
  // Filter instruments
  let filtered = filterInstruments(instruments, parsedQuery);
  
  // Sort by type and expiry, with relevance as a tiebreaker
  const underlying = parsedQuery.underlying;
  const typeOrder = {
    'INDEX': 0,
    'EQ': 1,
    'FUTIDX': 2,
    'FUTSTK': 3,
    'FUT': 4,
    'OPTIDX': 5,
    'OPTSTK': 6,
    'CE': 7,
    'PE': 8
  };
  
  filtered.sort((a, b) => {
    const typeA = a.instrument_type || a.segment || 'ZZZ';
    const typeB = b.instrument_type || b.segment || 'ZZZ';
    
    // First sort by instrument type
    const orderA = typeOrder[typeA] ?? 99;
    const orderB = typeOrder[typeB] ?? 99;
    
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    
    // Within same type, prioritize exact underlying match (NIFTY before FINNIFTY)
    if (underlying && sortByRelevance) {
      const symA = (a.tradingsymbol || a.trading_symbol || '').toUpperCase();
      const symB = (b.tradingsymbol || b.trading_symbol || '').toUpperCase();
      const aStartsWith = symA.startsWith(underlying);
      const bStartsWith = symB.startsWith(underlying);
      
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
    }
    
    // For options, sort CE before PE
    if (typeA === 'CE' && typeB === 'PE') return -1;
    if (typeA === 'PE' && typeB === 'CE') return 1;
    
    // Then sort by expiry (nearest first)
    const expiryA = a.expiry ? new Date(a.expiry) : null;
    const expiryB = b.expiry ? new Date(b.expiry) : null;
    
    if (expiryA && expiryB) {
      const diff = expiryA - expiryB;
      if (diff !== 0) return diff;
    }
    
    // Then by strike price
    const strikeA = a.strike || 0;
    const strikeB = b.strike || 0;
    if (strikeA !== strikeB) return strikeA - strikeB;
    
    // Finally by symbol name
    const sym1 = a.tradingsymbol || a.trading_symbol || '';
    const sym2 = b.tradingsymbol || b.trading_symbol || '';
    return sym1.localeCompare(sym2);
  });
  
  // Limit results
  if (limit && filtered.length > limit) {
    filtered = filtered.slice(0, limit);
  }
  
  // Group results
  const grouped = groupInstruments(filtered);
  
  return {
    instruments: filtered,
    grouped,
    query: parsedQuery.raw,
    parsedQuery
  };
};

// Format instrument for display
export const formatInstrumentDisplay = (inst) => {
  const symbol = inst.tradingsymbol || inst.trading_symbol || inst.name || 'Unknown';
  const type = inst.instrument_type || inst.segment || '';
  const exchange = inst.exchange || 'NSE';
  const expiry = inst.expiry ? new Date(inst.expiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
  const strike = inst.strike || '';
  
  let badge = '';
  if (type === 'EQ' || type === 'NSE_EQ') badge = 'EQ';
  else if (type.includes('FUT')) badge = 'FUT';
  else if (type === 'CE' || symbol.endsWith('CE')) badge = 'CE';
  else if (type === 'PE' || symbol.endsWith('PE')) badge = 'PE';
  else if (type === 'INDEX') badge = 'IDX';
  
  return {
    symbol,
    displayName: symbol,
    badge,
    exchange,
    expiry,
    strike,
    type
  };
};

export default {
  parseSearchQuery,
  globalSearch,
  groupInstruments,
  sortInstruments,
  filterInstruments,
  formatInstrumentDisplay
};
